import React, { useMemo, useState } from 'react';
import Button from '../../components/Button';
import { __ } from '../../core/utils';
import * as _ from 'lodash';
import styles from '../../styles/forms/create_folder_wizard/CreateFolderForm.scss';
import CheckedIcon from '../../assets/images/checked-15x15.svg';
import UncheckedIcon from '../../assets/images/unchecked-15x15.svg';
import NeutralIcon from '../../assets/images/neutral-15x15.svg';
import FolderClosedIcon from '../../assets/images/folder-closed-16x16.svg';
import FolderOpenedIcon from '../../assets/images/folder-opened-16x16.svg';
import classNames from 'classnames';

const getInitialStatus = (folder, planIds) => {
  const folderPlanIds = _.map(folder.plan_ids || [], _.toString);
  const selectedPlanIds = _.map(planIds, _.toString);
  const matchingPlanIds = _.intersection(folderPlanIds, selectedPlanIds);

  if (_.isEmpty(matchingPlanIds)) {
    return 'unchecked';
  }

  return _.size(matchingPlanIds) === _.size(selectedPlanIds)
    ? 'checked'
    : 'neutral';
};

const nextStatus = (status) => {
  if (status === 'checked') {
    return 'unchecked';
  }

  if (status === 'unchecked') {
    return 'checked';
  }

  return 'checked';
};

const AttachPlansToFoldersForm = ({
  folders = [],
  plan_ids = [],
  onClose,
  onSubmit,
  submitting,
}) => {
  const initialStatuses = useMemo(() => {
    const statuses = {};

    const collectStatuses = (items) => {
      _.each(items, (folder) => {
        statuses[folder.id] = getInitialStatus(folder, plan_ids);
        collectStatuses(folder.children || []);
      });
    };

    collectStatuses(folders);

    return statuses;
  }, [folders, plan_ids]);

  const [statuses, setStatuses] = useState(initialStatuses);
  const [expanded, setExpanded] = useState({});

  const getStatus = (folder) => statuses[folder.id] || 'unchecked';

  const toggleStatus = (folder) => {
    setStatuses({
      ...statuses,
      [folder.id]: nextStatus(getStatus(folder)),
    });
  };

  const toggleExpanded = (folder) => {
    setExpanded({
      ...expanded,
      [folder.id]: expanded[folder.id] === false,
    });
  };

  const renderCheckbox = (folder) => {
    const status = getStatus(folder);
    const Icon =
      status === 'checked'
        ? CheckedIcon
        : status === 'neutral'
        ? NeutralIcon
        : UncheckedIcon;

    return (
      <Icon
        className={classNames(
          styles.checkbox,
          status === 'checked' && styles.checked,
          status === 'neutral' && styles.neutral
        )}
        onClick={(e) => {
          e.stopPropagation();
          toggleStatus(folder);
        }}
      />
    );
  };

  const renderFolder = (folder, level = 0) => {
    const isExpanded = expanded[folder.id] !== false;
    const hasChildren = !_.isEmpty(folder.children);
    const FolderIcon =
      hasChildren && isExpanded ? FolderOpenedIcon : FolderClosedIcon;

    return (
      <React.Fragment key={folder.id}>
        <div
          className={styles.folder}
          style={{ paddingLeft: 20 + level * 20 }}
          onClick={() => toggleStatus(folder)}
        >
          <div
            className={styles.folderContent}
            onClick={(e) => {
              e.stopPropagation();
              hasChildren && toggleExpanded(folder);
            }}
          >
            <FolderIcon className={styles.folderIcon} />
            <span>{folder.title}</span>
          </div>
          {renderCheckbox(folder)}
        </div>
        {hasChildren &&
          isExpanded &&
          _.map(folder.children, (child) => renderFolder(child, level + 1))}
      </React.Fragment>
    );
  };

  const handleSubmit = () => {
    const folder_ids = [];
    const removed_folder_ids = [];

    _.each(statuses, (status, folderId) => {
      const initialStatus = initialStatuses[folderId] || 'unchecked';

      if (status === 'checked' && initialStatus !== 'checked') {
        folder_ids.push(folderId);
      }

      if (status === 'unchecked' && initialStatus !== 'unchecked') {
        removed_folder_ids.push(folderId);
      }
    });

    onSubmit({ folder_ids, removed_folder_ids });
  };

  return (
    <form onSubmit={handleSubmit} className={styles.wrapper}>
      <div className={styles.folderChooser}>
        {_.map(folders, (folder) => renderFolder(folder))}
      </div>
      <div className={styles.footer}>
        <Button
          lightGray
          medium
          left
          middleText={__('button.cancel')}
          onClick={onClose}
        />
        <Button
          lightBlue
          medium
          middleText={__('button.add')}
          spinning={submitting}
          onClick={handleSubmit}
        />
      </div>
    </form>
  );
};

export default AttachPlansToFoldersForm;
